import React, { useEffect, useState } from "react";
import Button from "../../components/elements/Button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BULK_ADD_INVOICE } from "../../redux/type";
import { bulkAddInvoiceAction } from "../../redux/actions/invoiceAction";
import { parseExcelFile, validateInvoiceData, InvoiceRow } from "../../helper/ExcelParser";
import Table from "../../components/elements/Table";
import { GridColDef } from "@mui/x-data-grid";
import Loader from "../../components/elements/Loader";
import moment from "moment";
import "./BulkInvoiceUpload.css";

const BulkInvoiceUpload = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();
  const firmId = location.state?.firmId || params.firmId;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceRow[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showTable, setShowTable] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const [partialFailures, setPartialFailures] = useState<{ index: number; reason: string }[]>([]);

  const bulkAddInvoiceData = useSelector(
    (state: any) => state.bulkAddInvoice.bulkAddInvoice
  );

  useEffect(() => {
    if (bulkAddInvoiceData) {
      if (bulkAddInvoiceData.status === 200) {
        toast.success(bulkAddInvoiceData.message);
        navigate(`/firm/edit/${firmId}`, { state: { activeTab: 1 } });
      } else if (bulkAddInvoiceData.status === 207) {
        toast.info(bulkAddInvoiceData.message);
        setPartialFailures(bulkAddInvoiceData?.data?.failures || []);
        setPartialOpen(true);
      } else {
        toast.error(bulkAddInvoiceData.message);
      }
      dispatch({ type: BULK_ADD_INVOICE, payload: null });
    }
  }, [bulkAddInvoiceData]);

  useEffect(() => {
    return () => {
      dispatch({
        type: BULK_ADD_INVOICE,
        payload: null,
      });
    };
  }, []);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = [
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel",
    ];

    if (!validTypes.includes(file.type)) {
      toast.error("Please upload a valid Excel file (.xlsx or .xls)");
      return;
    }

    setSelectedFile(file);
    setIsProcessing(true);
    setValidationErrors([]);
    setShowTable(false);

    try {
      const parsedData = await parseExcelFile(file);

      if (parsedData.length === 0) {
        toast.error("Excel file is empty");
        setIsProcessing(false);
        return;
      }

      const validation = validateInvoiceData(parsedData);

      if (!validation.valid) {
        setValidationErrors(validation.errors);
        toast.error(`Found ${validation.errors.length} validation errors`);
        setIsProcessing(false);
        return;
      }

      setInvoiceData(parsedData);
      setShowTable(true);
      toast.success(`Successfully loaded ${parsedData.length} invoices`);
    } catch (error: any) {
      toast.error(error.message || "Failed to parse Excel file");
      setInvoiceData([]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSubmit = () => {
    if (invoiceData.length === 0) {
      toast.error("No invoice data to submit");
      return;
    }

    const payload = {
      firmId: firmId,
      invoices: invoiceData,
    };
    dispatch(bulkAddInvoiceAction(payload));
  };

  const handleCancel = () => {
    navigate(`/firm/edit/${firmId}`, { state: { activeTab: 1 } });
  };

  const handlePartialOk = () => {
    setPartialOpen(false);
    navigate(`/firm/edit/${firmId}`, { state: { activeTab: 1 } });
  };

  const columns: GridColDef[] = [
    { field: "client", headerName: "Client", flex: 1 },
    {
      field: "invoiceDate",
      headerName: "Date",
      flex: 1,
      renderCell: ({ row }) => {
        return <>{moment(row.invoiceDate).format("DD/MM/YYYY")}</>;
      },
    },
    { field: "amount", headerName: "Amount", flex: 1 },
    { field: "rate", headerName: "Rate", flex: 1 },
    { field: "currency", headerName: "Currency", flex: 1 },
    { field: "bank", headerName: "Bank", flex: 1 },
    { field: "transactionId", headerName: "Transaction Id", flex: 1 },
  ];

  return (
    <div className="">
      <div className="card_header">
        <h1 className="card_header_title">Bulk Upload Invoice</h1>
        <div className="card_footer">
          <Button
            variant="outlined"
            buttonClass="contained_button_cancel"
            onClick={handleCancel}
            child="Cancel"
          />
          {showTable && (
            <Button
              onClick={handleSubmit}
              variant="outlined"
              buttonClass="contained_button_add"
              child="Submit"
            />
          )}
        </div>
      </div>

      {isProcessing && <Loader />}

      <div className="bulk_upload_container">
        <div className="file_upload_section">
          <h3>Upload Excel File</h3>
          <p className="upload_description">
            Upload an Excel file. The file should contain the following columns:
            client, invoiceDate, amount, rate, currency, bank.
          </p>
          <div className="file_input_wrapper">
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={isProcessing}
              className="file_input"
              id="excel_file_input"
            />
            <label htmlFor="excel_file_input" className="file_input_label">
              {selectedFile ? selectedFile.name : "Choose Excel File"}
            </label>
          </div>
        </div>

        {validationErrors.length > 0 && (
          <div className="validation_errors">
            <h4>Validation Errors:</h4>
            <ul>
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>
        )}

        {showTable && invoiceData.length > 0 && (
          <div className="table_section">
            <h3>Preview ({invoiceData.length} invoices)</h3>
            <Table
              rows={invoiceData.map((item: any, i: number) => ({
                ...item,
                id: i + 1,
              }))}
              columns={columns}
            />
          </div>
        )}
      </div>

      {partialOpen && (
        <div className="modal_overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1300 }}>
          <div className="modal_card" style={{ background: '#fff', borderRadius: 8, padding: 24, width: '90%', maxWidth: 700, maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>Adding Invoices Failed!</h3>
            <p>Some invoices failed to upload. Review the reasons below.</p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Index</th>
                    <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {partialFailures && partialFailures.length > 0 ? (
                    partialFailures.map((f) => (
                      <tr key={f.index}>
                        <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px', width: 100 }}>{f.index + 1}</td>
                        <td style={{ borderBottom: '1px solid #f0f0f0', padding: '8px' }}>{f.reason}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} style={{ padding: '12px' }}>No failures reported.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="modal_footer" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <Button onClick={handlePartialOk} variant="outlined" buttonClass="contained_button_add" child="OK" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkInvoiceUpload;
